
/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { useMemo } from "react";
import { Button } from "../../../../components/ui/button";
import { Icon } from "../../../../components/Shared/Icon";
import { IUserFileMgmt } from "../../../../store/api/admin/types";
import CustomDataGrid from "../../../../components/Shared/Table/CustomDataGrid";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { useSelector } from "react-redux";
import { basicDialogSelector, confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import ConfirmDialog from "../../../../components/Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../../globals/constants";
import { useExternalDisplayUserFileMgmt } from "./hooks";
import AddEditExternalUserFileMgmt from "../../../../components/Administrations/FileManagements/ExternalUsers/AddEditExternalUserFileMgmt";
import { externalUserFileMgmt } from "../../../../components/Shared/Table/Columns";
import { useStoreDispatch } from "../../../../store/hooks";
import SearchBox from "../../../../components/Shared/SearchBox/SearchBox";
import ActiveFilterChips from "../../../../components/Shared/Filter/ActiveFilterChips";
import { searchKeywordByScopeSelector } from "../../../../store/search/selector";
import { resetKeywordForScope } from "../../../../store/search/reducer";

const FILE_MGMT_EXTERNAL_USERS_SEARCH_SCOPE = "file_mgmt_external_users_grid_search";

const ExternalUsersFileMgmt: React.FC = () => {
    const dispatch = useStoreDispatch();
    const { data, isLoading, usersFileMgmtColumn, addUserFileMgmt, deleteHandler, selectedUserFileMgmt } = useExternalDisplayUserFileMgmt();
    const basicDialogOpenFor = useSelector(basicDialogSelector);
    const openConfirmDialog = useSelector(confirmDialogSelector);
    const searchKeyword = useSelector(searchKeywordByScopeSelector(FILE_MGMT_EXTERNAL_USERS_SEARCH_SCOPE));
    const rowsData = data || [];
    
    const filteredRowsData = useMemo(() => {
        if (!searchKeyword) return rowsData;
        const lowerFilter = searchKeyword.toLowerCase();
        return rowsData.filter((row: IUserFileMgmt) => 
            row.username.toLowerCase().includes(lowerFilter) || 
            (row.key && row.key.toLowerCase().includes(lowerFilter))
        );
    }, [rowsData, searchKeyword]);

    const noOfRows = filteredRowsData.length;

    return (
        <div className="flex flex-col justify-center items-start w-full">
            {openConfirmDialog === DIALOGOPENFOR.DELETEEXTERNALUSER && (
                <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteHandler}>
                    Are you sure you want to delete the user <strong>{selectedUserFileMgmt.username || ""}</strong>?
                </ConfirmDialog>
            )}
            {basicDialogOpenFor === DIALOGOPENFOR.EXTERNALUSERFILEMGMT && <AddEditExternalUserFileMgmt />}
            
            <div className="flex items-center justify-between w-full gap-2 px-3 mt-4">
                <div className="flex w-full max-w-sm items-center space-x-2">
                    <SearchBox 
                        tooltip="Search by user name or SSH-Key..." 
                        placeholder="Search external users..." 
                        scope={FILE_MGMT_EXTERNAL_USERS_SEARCH_SCOPE} 
                        width={270} 
                    />
                </div>

                <Button type="button" onClick={addUserFileMgmt} className="bg-primary text-primary-foreground shadow hover:bg-primary/80">
                    <Icon name="add" size={18} />
                    Add External User
                </Button>
            </div>

            <div className="px-3 pb-1 pt-8 flex items-center min-h-[36px] w-full shrink-0">
                <ActiveFilterChips
                    filters={[]}
                    resultCount={noOfRows}
                    searchKeyword={searchKeyword}
                    onClearSearch={() => dispatch(resetKeywordForScope({ scope: FILE_MGMT_EXTERNAL_USERS_SEARCH_SCOPE }))}
                    onClearAll={() => dispatch(resetKeywordForScope({ scope: FILE_MGMT_EXTERNAL_USERS_SEARCH_SCOPE }))}
                    dispatch={dispatch}
                />
            </div>
            <div className="w-full pl-3 pr-3">
                <CustomDataGrid
                    tableId="extUserFileMgmt"
                    rows={filteredRowsData}
                    columns={externalUserFileMgmt}
                    checkboxSelection={false}
                    rowCount={noOfRows}
                    loading={isLoading}
                    disableColumnMenu={true}
                    disableRowSelectionOnClick={true}
                    getRowId={(row: IUserFileMgmt) => row.id}
                    paginationMode="client"
                    fillContainer
                />
            </div>
        </div>
    );
};
export default ExternalUsersFileMgmt;